import React, { useState, useEffect, useCallback } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { calendarAPI } from '../services/api';
import { CalendarEntry, Statistics } from '../types';
import CalendarModal from '../components/CalendarModal';
import StatisticsPanel from '../components/StatisticsPanel';
import Header from '../components/Header';

const Calendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { user } = useAuth();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // 获取当月第一天是星期几（0=周日，1=周一，...，6=周六）
  const firstDayOfWeek = monthStart.getDay();
  
  // 创建完整的日历日期数组，包括月初的空白格子
  const calendarDays = [
    // 添加月初的空白格子
    ...Array(firstDayOfWeek).fill(null),
    // 添加当月的所有日期
    ...monthDays
  ];

  const fetchEntries = useCallback(async () => {
    try {
      setIsLoading(true);
      const month = format(currentDate, 'yyyy-MM');
      const data = await calendarAPI.getEntries(month);
      setEntries(data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate]);

  const fetchStatistics = useCallback(async () => {
    try {
      const month = format(currentDate, 'yyyy-MM');
      const data = await calendarAPI.getStatistics(month);
      setStatistics(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchEntries();
    fetchStatistics();
  }, [fetchEntries, fetchStatistics]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'locked':
        return 'bg-green-500';
      case 'unlocked':
        return 'bg-red-500';
      case 'ruined':
        return 'bg-yellow-500';
      case 'orgasm':
        return 'bg-black';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'locked':
        return '🔒';
      case 'unlocked':
        return '🔑';
      case 'ruined':
        return '🔒🐍';
      case 'orgasm':
        return '🐍';
      default:
        return '未知';
    }
  };

  const getEntryForDate = (date: Date): CalendarEntry | null => {
    return entries.find(entry => isSameDay(new Date(entry.date), date)) || null;
  };

  const handleDateClick = (date: Date) => {
    // 检查是否为未来日期
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    const isFutureDate = selectedDate > today;
    
    // 权限规则：
    // - 普通用户：始终不能编辑
    // - 管理员：可以编辑今天和今天以前的日期，但不能编辑未来日期
    // - 所有人：都不能编辑未来日期
    const canEditDate = user?.role === 'admin' && !isFutureDate;
    
    setSelectedDate(date);
    setSelectedEntry(getEntryForDate(date));
    setCanEdit(canEditDate);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedDate(null);
    setSelectedEntry(null);
    setCanEdit(false);
    fetchEntries();
    fetchStatistics();
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {format(currentDate, 'yyyy年 MMMM', { locale: zhCN })}
              </h1>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousMonth}
                  className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {!user && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-blue-800">
                    <p className="font-medium">您正在以访客模式浏览管理员的日历记录</p>
                    <p className="text-sm">请<a href="/login" className="text-blue-600 hover:text-blue-800 underline">登录</a>以查看和编辑您的个人记录</p>
                  </div>
                </div>
              </div>
            )}

            {statistics && <StatisticsPanel statistics={statistics} />}

            <div className="mt-6">
              <div className="grid grid-cols-7 gap-px bg-gray-200">
                {weekDays.map(day => (
                  <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-700">
                    {day}
                  </div>
                ))}
                {calendarDays.map((day, index) => {
                  // 如果是空白格子，不渲染任何内容
                  if (!day) {
                    return (
                      <div
                        key={`empty-${index}`}
                        className="bg-gray-50 p-2 min-h-[80px]"
                      />
                    );
                  }
                  
                  const entry = getEntryForDate(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div
                      key={index}
                      onClick={() => handleDateClick(day)}
                      className={`bg-white p-2 min-h-[80px] cursor-pointer hover:bg-gray-50 ${
                        isToday ? 'ring-2 ring-blue-500' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {format(day, 'd')}
                      </div>
                      {entry && (
                        <div className="mt-1">
                          <div className={`w-4 h-4 rounded-full ${getStatusColor(entry.status)} mx-auto`}></div>
                          <div className="text-xs text-center mt-1 text-gray-600">
                            {getStatusText(entry.status)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                <span>戴锁</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                <span>解锁</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
                <span>🔒🐍</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-gray-600 mr-2"></div>
                <span>🐍🐳</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && selectedDate && (
        <CalendarModal
          date={selectedDate}
          entry={selectedEntry}
          onClose={handleModalClose}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default Calendar;